/**
 * Card WL — Outras Configurações (Widget de Suporte Interno + Acesso Remoto WebRTC)
 *
 * 100% server-side via RPCs WL:
 *   - wl_get_support_widget / wl_update_support_widget (config)
 *   - wl_list_tenants (via useWlTenantSearch) — busca server-side de tenants
 *   - wl_list_tenant_inboxes — inboxes do tenant selecionado
 *
 * Popovers de pesquisa:
 *   - Tenant: replica o popover do WlTenantFilter (busca server-side + debounce 300ms),
 *     mas o trigger mantém o formato de campo de formulário (full-width, com Label).
 *   - Inbox: replica o popover usado em ServiceQueue (Command com busca local + ícone do
 *     canal por inbox), também com trigger em formato de campo de formulário.
 *
 * @version 1.1.0
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Settings,
  MessageCircle,
  Building2,
  Inbox,
  Monitor,
  Loader2,
  ChevronDown,
  Check,
  MessageSquare,
  Instagram,
  MessagesSquare,
  Send,
  Globe,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useWlSupportWidget } from '@/hooks/wl/useWlSupportWidget';
import {
  useWlTenantSearch,
  type WlTenantSearchItem,
} from '@/hooks/wl/useWlTenantSearch';
import { useWlPermissions } from '@/contexts/WlPermissionsContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Ícone do canal para uma inbox (mesmo mapeamento usado em ServiceQueue). */
function InboxChannelIcon({ channel, size = 14 }: { channel?: string | null; size?: number }) {
  const sz = `h-[${size}px] w-[${size}px]`;
  switch (channel) {
    case 'whatsapp':
    case 'whatsapp_oficial':
      return <MessageSquare className={cn(sz, 'text-green-600')} />;
    case 'instagram':
      return <Instagram className={cn(sz, 'text-pink-500')} />;
    case 'messenger':
      return <MessagesSquare className={cn(sz, 'text-blue-500')} />;
    case 'telegram':
      return <Send className={cn(sz, 'text-sky-500')} />;
    case 'webchat':
      return <Globe className={cn(sz, 'text-muted-foreground')} />;
    default:
      return <Inbox className={cn(sz, 'text-muted-foreground')} />;
  }
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function WlSupportSettingsCard() {
  const { toast } = useToast();
  const { canEdit } = useWlPermissions();
  const canEditSettings = canEdit('wl.settings');

  const {
    config: supportConfig,
    updateConfig: updateSupportConfig,
    isUpdating: isSavingSupport,
    isLoading: isLoadingConfig,
  } = useWlSupportWidget();

  // Estados locais
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [selectedInbox, setSelectedInbox] = useState<string>('');
  const [remoteAccessEnabled, setRemoteAccessEnabled] = useState<boolean>(false);

  // Cache do tenant selecionado (para exibir o nome no trigger sem refetch)
  const [selectedTenantCache, setSelectedTenantCache] =
    useState<WlTenantSearchItem | null>(null);

  // Popovers
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false);
  const [inboxPopoverOpen, setInboxPopoverOpen] = useState(false);

  // Busca de tenants (debounce 300ms, igual ao WlTenantFilter)
  const [tenantRawSearch, setTenantRawSearch] = useState('');
  const [tenantDebounced, setTenantDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTenantDebounced(tenantRawSearch), 300);
    return () => clearTimeout(t);
  }, [tenantRawSearch]);

  const { data: tenantResults = [], isFetching: isFetchingTenants } =
    useWlTenantSearch(tenantDebounced, tenantPopoverOpen);

  // Sincroniza config salva → estados locais (uma vez)
  useEffect(() => {
    if (supportConfig.tenant_id && !selectedTenant) {
      setSelectedTenant(supportConfig.tenant_id);
    }
    if (supportConfig.inbox_id && !selectedInbox) {
      setSelectedInbox(supportConfig.inbox_id);
    }
  }, [supportConfig.tenant_id, supportConfig.inbox_id, selectedTenant, selectedInbox]);

  useEffect(() => {
    setRemoteAccessEnabled(!!supportConfig.remote_access_enabled);
  }, [supportConfig.remote_access_enabled]);

  // Inicializa cache do tenant a partir do nome salvo na config (sem fetch extra)
  useEffect(() => {
    if (
      supportConfig.tenant_id &&
      supportConfig.tenant_name &&
      !selectedTenantCache
    ) {
      setSelectedTenantCache({
        id: supportConfig.tenant_id,
        name: supportConfig.tenant_name,
        slug: null,
        owner_name: null,
        owner_email: null,
        owner_phone_masked: null,
      });
    }
  }, [supportConfig.tenant_id, supportConfig.tenant_name, selectedTenantCache]);

  // Inboxes do tenant ativo via RPC WL
  const activeTenantId = selectedTenant || supportConfig.tenant_id;
  const { data: inboxes = [], isLoading: isLoadingInboxes } = useQuery({
    queryKey: ['wl', 'inboxes-for-support-widget', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return [] as Array<{
        inbox_id: string;
        name: string;
        status: string;
        channel_type: string | null;
      }>;
      const { data, error } = await supabase.rpc('wl_list_tenant_inboxes', {
        p_tenant_id: activeTenantId,
      });
      if (error) throw error;
      return ((data || []) as Array<{
        inbox_id: string;
        name: string;
        status: string;
        channel_type: string | null;
      }>).filter((i) => i.status === 'active');
    },
    enabled: !!activeTenantId,
  });

  // Inbox atualmente selecionada (objeto)
  const selectedInboxObj = useMemo(
    () => inboxes.find((i) => i.inbox_id === selectedInbox) || null,
    [inboxes, selectedInbox],
  );

  // Label do trigger de tenant
  const tenantTriggerLabel =
    selectedTenantCache?.name ||
    (selectedTenant ? 'Tenant selecionado' : 'Selecione o tenant');

  // ----- Handlers -----
  const handleSelectTenant = (t: WlTenantSearchItem) => {
    setSelectedTenant(t.id);
    setSelectedTenantCache(t);
    setSelectedInbox(''); // limpa inbox ao trocar tenant
    setTenantPopoverOpen(false);
    setTenantRawSearch('');
  };

  const handleSelectInbox = (inboxId: string) => {
    setSelectedInbox(inboxId);
    setInboxPopoverOpen(false);
  };

  const handleSaveSupportWidget = async () => {
    const inbox = inboxes.find((i) => i.inbox_id === selectedInbox);

    if (!selectedInbox) {
      toast({
        title: 'Selecione uma inbox',
        description: 'É necessário selecionar uma inbox para o widget de suporte.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateSupportConfig({
        enabled: true,
        tenant_id: selectedTenant,
        inbox_id: selectedInbox,
        tenant_name: selectedTenantCache?.name || null,
        inbox_name: inbox?.name || null,
      });
    } catch {
      /* erro já tratado no hook */
    }
  };

  const handleDisableWidget = async () => {
    try {
      await updateSupportConfig({ enabled: false });
    } catch {
      /* erro já tratado no hook */
    }
  };

  const handleRemoteAccessToggle = async (checked: boolean) => {
    try {
      await updateSupportConfig({ remote_access_enabled: checked });
      setRemoteAccessEnabled(checked);
    } catch {
      /* erro já tratado no hook */
    }
  };

  const disabledByPerm = !canEditSettings;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Outras Configurações
        </CardTitle>
        <CardDescription>Configurações adicionais do sistema</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* ----------------------------------------------------------------- */}
        {/* Widget de Suporte Interno                                         */}
        {/* ----------------------------------------------------------------- */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            <Label className="text-base font-medium">Widget de Suporte Interno</Label>
          </div>
          <p className="text-sm text-muted-foreground">
            Configure qual tenant e inbox receberá as mensagens de suporte dos usuários
            do sistema. O widget aparecerá em todas as páginas do tenant (Conversas e CRM).
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            {/* ----- Tenant (popover server-side igual à página de auditoria) ----- */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                Tenant
              </Label>
              <Popover
                open={tenantPopoverOpen}
                onOpenChange={(o) => {
                  setTenantPopoverOpen(o);
                  if (!o) setTenantRawSearch('');
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={isLoadingConfig || disabledByPerm}
                    className="w-full justify-between font-normal h-10"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Building2 className="h-4 w-4 opacity-70 shrink-0" />
                      <span className="truncate">{tenantTriggerLabel}</span>
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="start">
                  {/* shouldFilter=false: filtragem 100% server-side */}
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Buscar por nome, slug, email, telefone..."
                      value={tenantRawSearch}
                      onValueChange={setTenantRawSearch}
                    />
                    <CommandList>
                      {isFetchingTenants && tenantResults.length === 0 ? (
                        <div className="py-6 text-center text-xs text-muted-foreground">
                          Buscando...
                        </div>
                      ) : (
                        <CommandEmpty>Nenhum tenant encontrado.</CommandEmpty>
                      )}

                      {tenantResults.length > 0 && (
                        <CommandGroup
                          heading={
                            tenantDebounced
                              ? `Resultados (${tenantResults.length})`
                              : '10 mais recentes'
                          }
                        >
                          {tenantResults.map((t) => {
                            const isSel = selectedTenant === t.id;
                            return (
                              <CommandItem
                                key={t.id}
                                value={t.id}
                                onSelect={() => handleSelectTenant(t)}
                                className="cursor-pointer"
                              >
                                <div
                                  className={cn(
                                    'mr-2 h-4 w-4 rounded-full border flex items-center justify-center shrink-0',
                                    isSel
                                      ? 'border-primary bg-primary'
                                      : 'border-muted',
                                  )}
                                >
                                  {isSel && (
                                    <Check className="h-2.5 w-2.5 text-primary-foreground" />
                                  )}
                                </div>
                                <div className="flex flex-col flex-1 min-w-0">
                                  <span className="truncate text-sm">{t.name}</span>
                                  <span className="text-[11px] text-muted-foreground truncate">
                                    {[
                                      t.slug ? `@${t.slug}` : null,
                                      t.owner_name,
                                      t.owner_email,
                                      t.owner_phone_masked,
                                    ]
                                      .filter(Boolean)
                                      .join(' · ') || '—'}
                                  </span>
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* ----- Inbox (popover estilo ServiceQueue, busca local) ----- */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Inbox className="h-3.5 w-3.5" />
                Inbox
              </Label>
              <Popover open={inboxPopoverOpen} onOpenChange={setInboxPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={!activeTenantId || isLoadingInboxes || disabledByPerm}
                    className="w-full justify-between font-normal h-10"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      {selectedInboxObj ? (
                        <InboxChannelIcon
                          channel={selectedInboxObj.channel_type}
                          size={14}
                        />
                      ) : (
                        <Inbox className="h-4 w-4 opacity-70 shrink-0" />
                      )}
                      <span className="truncate">
                        {selectedInboxObj?.name ||
                          (!activeTenantId
                            ? 'Selecione o tenant primeiro'
                            : isLoadingInboxes
                              ? 'Carregando...'
                              : 'Selecione a inbox')}
                      </span>
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar inbox..." />
                    <CommandList>
                      <CommandEmpty>Nenhuma inbox encontrada.</CommandEmpty>
                      {inboxes.length > 0 && (
                        <>
                          <CommandSeparator />
                          <CommandGroup heading="Inboxes">
                            {inboxes.map((inbox) => {
                              const isSel = selectedInbox === inbox.inbox_id;
                              return (
                                <CommandItem
                                  key={inbox.inbox_id}
                                  value={inbox.name}
                                  onSelect={() => handleSelectInbox(inbox.inbox_id)}
                                  className="cursor-pointer"
                                >
                                  <div
                                    className={cn(
                                      'mr-2 h-4 w-4 rounded-full border flex items-center justify-center',
                                      isSel
                                        ? 'border-primary bg-primary'
                                        : 'border-muted',
                                    )}
                                  >
                                    {isSel && (
                                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                                    )}
                                  </div>
                                  <span className="mr-2 inline-flex">
                                    <InboxChannelIcon
                                      channel={inbox.channel_type}
                                      size={14}
                                    />
                                  </span>
                                  <span className="truncate">{inbox.name}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Status atual + botões */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="text-sm">
              {supportConfig.enabled && supportConfig.inbox_name ? (
                <p className="text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium mr-2">
                    Ativo
                  </span>
                  <strong>{supportConfig.tenant_name}</strong> →{' '}
                  <strong>{supportConfig.inbox_name}</strong>
                </p>
              ) : (
                <p className="text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-medium mr-2">
                    Inativo
                  </span>
                  Nenhuma configuração ativa
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {supportConfig.enabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisableWidget}
                  disabled={isSavingSupport || disabledByPerm}
                >
                  Desabilitar
                </Button>
              )}
              <Button
                onClick={handleSaveSupportWidget}
                disabled={!selectedInbox || isSavingSupport || disabledByPerm}
                size="sm"
              >
                {isSavingSupport ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Salvar
              </Button>
            </div>
          </div>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Acesso Remoto (WebRTC)                                            */}
        {/* ----------------------------------------------------------------- */}
        <div className="space-y-4 pt-6 border-t">
          <div className="flex items-center gap-2">
            <Monitor className="h-5 w-5 text-primary" />
            <Label className="text-base font-medium">Acesso Remoto (WebRTC)</Label>
          </div>
          <p className="text-sm text-muted-foreground">
            Permite que atendentes do tenant de suporte solicitem visualização da tela
            dos usuários durante o atendimento.
            <br />
            <span className="text-xs text-warning">
              ⚠️ Requer tenant de suporte configurado acima.
            </span>
          </p>

          <div className="flex items-center justify-between py-3 px-4 rounded-lg border bg-muted/30">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Habilitar botão de Acesso Remoto</Label>
              <p className="text-xs text-muted-foreground">
                Quando ativo, um botão de "Visualizar Tela" aparece no chat do tenant de
                suporte.
              </p>
            </div>
            <Switch
              checked={remoteAccessEnabled}
              onCheckedChange={handleRemoteAccessToggle}
              disabled={!supportConfig.enabled || isSavingSupport || disabledByPerm}
            />
          </div>

          {!supportConfig.enabled && (
            <p className="text-xs text-muted-foreground italic">
              ℹ️ Configure e ative o Widget de Suporte acima para habilitar o Acesso Remoto.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
